import { Box, Stack } from "@mui/material";
import { IconCircleCheck } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../../components/ui/Button";
import Text from "../../../../components/ui/Text";
import {
  GAME_PAYMENT_MODE,
  GAME_STATUS,
  INVITE_STATUS,
} from "../../../../constants/game";
import { useAuth } from "../../../../contexts/AuthContext";
import { useToast } from "../../../../contexts/ToastContext";
import apiService from "../../../../services/api";
import { CancelGameConfirmationDialog } from "./CancelGameConfirmationDialog";
import { DeclineInviteDialog } from "./DeclineInviteDialog";
import { JoinGameDrawer } from "./JoinGameDrawer";
import { LeaveGameConfirmationDialog } from "./LeaveGameConfirmationDialog";

export const ActionButtons = ({
  gameDetails,
  players,
  waitlist = [],
  maxPlayers,
  fetchGameDetails,
  fetchPlayers,
  fetchWaitlist,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentUser, fetchUserProfile } = useAuth();

  const [isCancelGameModalOpen, setIsCancelGameModalOpen] = useState(false);
  const [isLeaveGameDialogOpen, setIsLeaveGameDialogOpen] = useState(false);
  const [isDeclineInviteDialogOpen, setIsDeclineInviteDialogOpen] =
    useState(false);
  const [joinDrawerState, setJoinDrawerState] = useState({
    isOpen: false,
    isInvite: false,
  });
  const [isWaitlistActionLoading, setIsWaitlistActionLoading] = useState(false);

  const isCurrentUserHost = currentUser?.id === gameDetails.created_by;
  const isCurrentUserInGame = players.some(
    (player) => player.player_id === currentUser?.id,
  );
  const isCurrentUserInvited = players.some(
    (player) =>
      player.player_id === currentUser?.id &&
      player.invite_status === INVITE_STATUS.PENDING,
  );

  const isGameFull = players.length >= maxPlayers;

  const currentUserWaitlistEntry = waitlist.find(
    (entry) => entry.user_id === currentUser?.id,
  );
  const isCurrentUserOnWaitlist = !!currentUserWaitlistEntry;

  const handleCancelGame = async () => {
    try {
      const response = await apiService.cancelGame(gameDetails.id);

      if (response.success) {
        showToast(response.message, "success");
        fetchGameDetails();
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsCancelGameModalOpen(false);
    }
  };

  const handleLeaveGame = async () => {
    try {
      const response = await apiService.leaveGame(gameDetails.id);

      if (response.success) {
        showToast(response.message, "success");
        fetchGameDetails();
        fetchPlayers();
        fetchUserProfile(currentUser?.accessToken);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsLeaveGameDialogOpen(false);
    }
  };

  if (gameDetails.status === GAME_STATUS.COMPLETED) {
    return (
      <Box>
        <Text
          component="p"
          variant="subtitle"
          sx={{
            fontWeight: 700,
            lineHeight: 1.5,
            mb: 1,
          }}
        >
          Game date has passed
        </Text>
        <Text
          component="p"
          variant="paragraph"
          sx={{
            fontSize: "16px",
            lineHeight: 1.5,
          }}
        >
          You can no longer join this game. View the group to see upcoming
          games.
        </Text>
      </Box>
    );
  } else if (gameDetails.status === GAME_STATUS.CANCELLED) {
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#FF4D4F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Text
              variant="paragraph"
              sx={{
                fontSize: "10px",
                color: "white",
                fontWeight: "bold",
              }}
            >
              !
            </Text>
          </Box>
          <Text
            variant="subtitle"
            sx={{
              fontWeight: 700,
              lineHeight: 1.5,
            }}
          >
            Game cancelled by the host
          </Text>
        </Box>
        <Text
          variant="paragraph"
          sx={{
            fontSize: "16px",
            lineHeight: 1.5,
          }}
        >
          Players that have paid through the app will receive a full refund in
          credits.
        </Text>
      </Box>
    );
  } else if (
    isCurrentUserHost &&
    gameDetails.status === GAME_STATUS.PUBLISHED
  ) {
    return (
      <>
        <Stack spacing={2}>
          <Button
            variant="primary"
            onClick={() => navigate(`/edit-game/${gameDetails.id}`)}
            sx={{
              fontSize: "16px",
              py: 1.5,
            }}
          >
            Edit game
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setIsCancelGameModalOpen(true);
            }}
            sx={{
              fontSize: "16px",
              py: 1.5,
              color: "#FF4D4F",
              borderColor: "#FF4D4F",
              "&:hover": {
                backgroundColor: "#FFF2F0",
                borderColor: "#FF4D4F",
              },
            }}
          >
            Cancel game
          </Button>
          <CancelGameConfirmationDialog
            isOpen={isCancelGameModalOpen}
            onClose={() => setIsCancelGameModalOpen(false)}
            onSubmit={handleCancelGame}
          />
        </Stack>
      </>
    );
  }

  const handleJoinWaitlist = async () => {
    setIsWaitlistActionLoading(true);
    try {
      const response = await apiService.joinWaitlist(gameDetails.id);
      if (response.success) {
        showToast(response.message || "Joined the waiting list", "success");
        fetchWaitlist();
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsWaitlistActionLoading(false);
    }
  };

  const handleLeaveWaitlist = async () => {
    setIsWaitlistActionLoading(true);
    try {
      const response = await apiService.leaveWaitlist(gameDetails.id);
      if (response.success) {
        showToast(response.message || "Left the waiting list", "success");
        fetchWaitlist();
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsWaitlistActionLoading(false);
    }
  };

  const handleJoinGame = () => {
    if (!currentUser) {
      navigate("/signin", { state: { from: window.location.pathname } });
      return;
    }
    if (isGameFull) {
      handleJoinWaitlist();
      return;
    }
    if (gameDetails.payment_mode === GAME_PAYMENT_MODE.SELF) {
      setJoinDrawerState({ isOpen: true, isInvite: false });
      return;
    }
    navigate("/review-booking", {
      state: {
        gameData: gameDetails,
      },
    });
  };

  const handleAcceptInvite = () => {
    if (gameDetails.payment_mode === GAME_PAYMENT_MODE.SELF) {
      setJoinDrawerState({ isOpen: true, isInvite: true });
      return;
    }
    navigate("/review-booking", {
      state: {
        gameData: gameDetails,
        isInvite: true,
      },
    });
  };

  const handleDeclineInvite = async () => {
    try {
      const response = await apiService.respondToGameInvite(gameDetails.id, {
        action: "decline",
      });

      if (response.success) {
        showToast(response.message, "success");
        fetchGameDetails();
        fetchPlayers();
        fetchUserProfile(currentUser?.accessToken);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsDeclineInviteDialogOpen(false);
    }
  };

  if (isCurrentUserInvited) {
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            mb: 2,
            gap: 1,
            alignItems: "center",
          }}
        >
          <Text>You’ve been invited to join this game.</Text>
        </Box>
        <Stack spacing={2}>
          <Button
            variant="secondary"
            onClick={handleAcceptInvite}
            sx={{
              width: "100%",
              py: 1.5,
              fontSize: "16px",
              borderColor: "#008236",
              backgroundColor: "#E6FFFA",
              color: "#008236",
              "&:hover": {
                backgroundColor: "#CCFBF1",
                borderColor: "#008236",
              },
            }}
          >
            Accept
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeclineInviteDialogOpen(true);
            }}
            sx={{
              width: "100%",
              py: 1.5,
              fontSize: "16px",
              borderColor: "#9F2D00",
              backgroundColor: "#FFF7E6",
              color: "#9F2D00",
              "&:hover": {
                backgroundColor: "#FFEDD5",
                borderColor: "#9F2D00",
              },
            }}
          >
            Decline
          </Button>
        </Stack>

        <DeclineInviteDialog
          isOpen={isDeclineInviteDialogOpen}
          onClose={() => setIsDeclineInviteDialogOpen(false)}
          onSubmit={handleDeclineInvite}
        />

        <JoinGameDrawer
          open={joinDrawerState.isOpen}
          onClose={() => setJoinDrawerState({ isOpen: false, isInvite: false })}
          gameDetails={gameDetails}
          isInvite={joinDrawerState.isInvite}
        />
      </Box>
    );
  }

  if (isCurrentUserInGame) {
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            mb: 2,
            gap: 1,
            alignItems: "center",
          }}
        >
          <IconCircleCheck size={20} stroke={2.5} />
          <Text>You're going</Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => {
            setIsLeaveGameDialogOpen(true);
          }}
          sx={{
            width: "100%",
            py: 1.5,
            fontSize: "16px",
          }}
        >
          Leave game
        </Button>

        <LeaveGameConfirmationDialog
          isOpen={isLeaveGameDialogOpen}
          onClose={() => setIsLeaveGameDialogOpen(false)}
          onSubmit={handleLeaveGame}
          gameDetails={gameDetails}
        />
      </Box>
    );
  }

  if (isCurrentUserOnWaitlist) {
    const waitlistPosition =
      waitlist.findIndex((entry) => entry.user_id === currentUser?.id) + 1;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            mb: 2,
            gap: 1,
            alignItems: "center",
          }}
        >
          <Text>
            {currentUserWaitlistEntry?.offered_at
              ? "A spot is available - join now!"
              : `You're #${waitlistPosition} on the waiting list`}
          </Text>
        </Box>
        <Stack spacing={2}>
          {currentUserWaitlistEntry?.offered_at &&
            gameDetails.payment_mode !== GAME_PAYMENT_MODE.SELF && (
              <Button
                variant="primary"
                onClick={handleJoinGame}
                sx={{
                  width: "100%",
                  py: 1.5,
                  fontSize: "16px",
                }}
              >
                {`Join game - €${gameDetails.amount_per_player.toFixed(2)}`}
              </Button>
            )}
          <Button
            variant="secondary"
            disabled={isWaitlistActionLoading}
            onClick={handleLeaveWaitlist}
            sx={{
              width: "100%",
              py: 1.5,
              fontSize: "16px",
            }}
          >
            Leave waiting list
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        variant="primary"
        onClick={handleJoinGame}
        sx={{
          width: "100%",
          py: 1.5,
          fontSize: "16px",
        }}
      >
        {isGameFull
          ? "Join waiting list"
          : `Join game - €${gameDetails.amount_per_player.toFixed(2)}`}
      </Button>

      <JoinGameDrawer
        open={joinDrawerState.isOpen}
        onClose={() => setJoinDrawerState({ isOpen: false, isInvite: false })}
        gameDetails={gameDetails}
        isInvite={joinDrawerState.isInvite}
      />
    </Box>
  );
};
